import { Box, Button, Card, Chip, Stack, Typography } from "@mui/material";

import { FindingCard } from "./FindingCard";
import { workspaceLabelOf } from "./evidenceFormat";
import { applyFilters, countBySeverity, IFilters, IGrouped } from "./groupFindings";
import { FindingDiffStatus, IEvidenceChange, ITarget, SECTION_LABEL } from "./raio-x.types";

interface IProps {
  grouped: IGrouped;
  status: Map<string, FindingDiffStatus>;
  filters: IFilters;
  workspaceLabels: Record<string, string>;
  evidenceChanges: Map<string, IEvidenceChange>;
  onOpenWorkspace: (target: ITarget) => void;
}

function TargetFindings({
  target,
  status,
  filters,
  workspaceLabels,
  evidenceChanges,
}: {
  target: ITarget;
  status: Map<string, FindingDiffStatus>;
  filters: IFilters;
  workspaceLabels: Record<string, string>;
  evidenceChanges: Map<string, IEvidenceChange>;
}) {
  const findings = applyFilters(target.findings, filters, status);
  if (target.skipped) return <Typography variant="body2" color="text.secondary">{target.skipReason}</Typography>;
  if (findings.length === 0) return <Typography variant="body2" color="text.secondary">Nenhum achado.</Typography>;
  return (
    <>
      {findings.map((f) => (
        <FindingCard
          key={f.fingerprint}
          finding={f}
          status={status.get(f.fingerprint)}
          evidenceChange={evidenceChanges.get(f.fingerprint)}
          workspaceLabels={workspaceLabels}
        />
      ))}
    </>
  );
}

export function RaioXSections({ grouped, status, filters, workspaceLabels, evidenceChanges, onOpenWorkspace }: IProps) {
  return (
    <Stack spacing={3}>
      {grouped.organization && (
        <Card sx={{ p: 2, boxShadow: "none", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 3 }}>
          {/* why: o cartão também traz transferências e endereçabilidade do CNPJ, não só cargos/conectores/certificado; título específico ficava desatualizado a cada bloco novo. */}
          <Typography variant="h6" sx={{ mb: 1 }}>Cabeçalho do CNPJ</Typography>
          <TargetFindings target={grouped.organization} status={status} filters={filters} workspaceLabels={workspaceLabels} evidenceChanges={evidenceChanges} />
        </Card>
      )}
      {grouped.products.map((p) => (
        <Card key={p.product} sx={{ p: 2, boxShadow: "none", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{SECTION_LABEL[p.product]}: {p.workspaces.length} contrato(s)</Typography>
          {p.groups.map((g, idx) => (
            <Box key={g.id} sx={{ mb: 2 }}>
              {/* why: mais de um grupo de endereçabilidade no mesmo produto é possível (activeAddressableGroupCount); sem numerar, os cartões ficam indistinguíveis. */}
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Endereçabilidade do Grupo de {SECTION_LABEL[p.product]}{p.groups.length > 1 ? ` ${idx + 1}` : ""}
              </Typography>
              <TargetFindings target={g} status={status} filters={filters} workspaceLabels={workspaceLabels} evidenceChanges={evidenceChanges} />
            </Box>
          ))}
          <Stack spacing={1}>
            {p.workspaces.map((ws) => {
              const visible = applyFilters(ws.findings, filters, status);
              const counts = countBySeverity(visible);
              return (
                <Stack key={ws.targetId} direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, borderRadius: 2, "&:hover": { backgroundColor: "rgba(48,36,225,0.06)" } }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{workspaceLabelOf(ws.targetId, workspaceLabels)}</Typography>
                    {ws.skipped ? (
                      <Typography variant="caption" color="text.secondary">{ws.skipReason}</Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        {counts.BLOCKER > 0 && <Chip size="small" color="error" label={`${counts.BLOCKER} bloqueiam`} />}
                        {counts.MAJOR > 0 && <Chip size="small" color="warning" label={`${counts.MAJOR} importantes`} />}
                        {counts.MINOR > 0 && <Chip size="small" label={`${counts.MINOR} menores`} />}
                        <Chip size="small" variant="outlined" label={`${counts.descriptive} para conferência`} />
                      </Stack>
                    )}
                  </Box>
                  <Button variant="outlined" size="small" onClick={() => onOpenWorkspace(ws)} disabled={ws.skipped}>Ver achados</Button>
                </Stack>
              );
            })}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
